/**
 * Terms Acceptance Modal (SEAL-style)
 * Multi-page legal document acceptance flow with signature capture
 * Uses inline styles matching the site theme (dark navy + neon green)
 *
 * LEGAL_VERSION bumps whenever the Terms/Privacy/Cookie text is materially
 * updated. The version is persisted with every e-sign record so we can prove
 * which version a tenant signed. The text itself is the /terms, /privacy and
 * /cookies page components (LEGAL_SECTIONS below) — there is no second copy.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TermsContent } from "../../main-component/TermsPage/TermsPage";
import { PrivacyContent } from "../../main-component/PrivacyPage/PrivacyPage";
import { CookieContent } from "../../main-component/CookiePolicyPage/CookiePolicyPage";

// Bumped when legal text materially changes. Persisted with each e-sign.
//
// 2026-08-12: section 5 (Google Account Data) rewritten — the previous text
// declared `gmail.readonly`, `drive.readonly` and `calendar.events`, none of
// which are on our Google client, and omitted Docs, Sheets, Ads, Analytics and
// Classroom entirely.
//
// 2026-08-12.2: section 8 gained the cloud-storage consent terms, brought over
// from axoix.in where they were published first. The `.2` suffix exists because
// a bare date cannot distinguish two materially different texts released on the
// same day, and customers had already been notified against the first one — the
// version has to identify the document, not the day.
//
// 2026-08-13: section 6 (Google User Data) corrected and extended ahead of the
// Google OAuth verification submission. `yt-analytics.readonly` and the older
// admin CHORUS connect were being requested and not declared; Business Profile
// tokens moved to the tenant database and the text still said otherwise; the
// deletion contact was an address that does not exist. New 6.3 states plainly
// that `gmail.modify` and `drive` are restricted permissions and what that
// obliges us to; new 6.6 discloses that the nightly encrypted backup is the one
// copy of Google user data that leaves our servers; new 6.7 states what cannot
// reach it — BYOK providers, the MCP connector, and our other integrations.
//
// 2026-09-09: `documents` and `spreadsheets` withdrawn from the Google client — Docs
// and Sheets run on drive.file plus the Google Picker. Section 5 also still advertised
// full Gmail mailbox and full Drive access, and claimed AXOIX undergoes a CASA
// assessment; all three had been untrue since 2026-08-17. Users accept THIS text, so
// it takes a version rather than a silent edit.
// 2026-08-19: YouTube moved to its own Google OAuth client, separate from the one
// serving Calendar, Drive, Docs, Sheets, Ads, Analytics, Business Profile and
// Classroom. Both sit in the same Cloud project and share one consent screen, so
// the scope list is unchanged — but a Google account now holds TWO grants with us,
// Google lists AXOIX twice at myaccount.google.com/permissions, and revoking one
// leaves the other fully live. That correction is the reason for this bump: the
// previous text would have led someone to believe they had cut off all access when
// they had cut off half. Supersedes 2026-08-18, which was never published.
//
// 2026-10-03: BYOK provider list corrected everywhere (13 text + speech, voice and
// image providers, not "OpenAI, Anthropic, Groq"), and the false "voice never
// leaves our servers" sentence fixed (BR-133). The modal stopped holding its own
// copy of the three documents — it now renders the page components themselves.
//
// Keep the date part equal to `lastUpdated` on /privacy; scripts/check-legal-version.js
// enforces it and allows the optional `.N` revision suffix.
export const LEGAL_VERSION = '2026-10-03';

// Signature font options
const SIGNATURE_FONTS = [
  { name: 'Dancing Script', family: "'Dancing Script', cursive" },
  { name: 'Great Vibes', family: "'Great Vibes', cursive" },
  { name: 'Pacifico', family: "'Pacifico', cursive" },
  { name: 'Satisfy', family: "'Satisfy', cursive" },
  { name: 'Sacramento', family: "'Sacramento', cursive" },
];

// Load Google Fonts
const loadGoogleFonts = () => {
  if (document.getElementById('signature-fonts-link')) return;
  const link = document.createElement('link');
  link.id = 'signature-fonts-link';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Satisfy&family=Sacramento&display=swap';
  document.head.appendChild(link);
};

// Site theme colors
const THEME = {
  body: '#00020f',
  panel: '#11121d',
  panelAlt: '#15192f',
  accent: '#00ff97',
  accentDim: 'rgba(0,255,151,0.15)',
  gradient: 'linear-gradient(52deg, #2c32fe 0%, #00a4af 49%, #00ff97 100%)',
  text: '#ffffff',
  textMuted: '#8b949e',
  textSecondary: '#c9d1d9',
  border: 'rgba(255,255,255,0.1)',
  borderLight: 'rgba(255,255,255,0.06)',
  danger: '#dc3545',
  warning: '#ffc107',
};

interface TermsAcceptanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (acceptanceData: AcceptanceData) => void;
  userInfo: {
    name: string;
    email: string;
    company?: string;
  };
  planName?: string;
}

export interface AcceptanceData {
  accepted: boolean;
  timestamp: string;
  signerName: string;
  signerEmail: string;
  signatureType: 'draw' | 'type' | 'upload';
  signaturePng: string;
  signatureText?: string;
  signatureFont?: string;
  sectionsViewed: {
    terms: boolean;
    privacy: boolean;
    cookies: boolean;
  };
  ipAddress?: string;
  userAgent?: string;
  legalVersion: string; // Which version of the legal text was signed
}

// The modal renders the SAME components as /terms, /privacy and /cookies — one
// copy of each document. Until 2026-10-03 it held its own hardcoded HTML strings,
// which drifted from the pages (it still named 3 BYOK providers after the pages
// moved on). Never reintroduce a second copy here.
const LEGAL_SECTIONS: Record<'terms' | 'privacy' | 'cookies', { title: string; icon: string; Body: React.FC }> = {
  terms: { title: 'Terms of Service', icon: 'fa-file-contract', Body: TermsContent },
  privacy: { title: 'Privacy Policy', icon: 'fa-shield-alt', Body: PrivacyContent },
  cookies: { title: 'Cookie Policy', icon: 'fa-cookie-bite', Body: CookieContent },
};

const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  userInfo,
  planName = 'AXOIX'
}) => {
  const [currentSection, setCurrentSection] = useState<'terms' | 'privacy' | 'cookies'>('terms');
  const [sectionsViewed, setSectionsViewed] = useState({ terms: false, privacy: false, cookies: false });
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('type');
  const [typedName, setTypedName] = useState(userInfo.name || '');
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0]);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [paths, setPaths] = useState<Array<Array<{x: number; y: number}>>>([]);
  const [currentPath, setCurrentPath] = useState<Array<{x: number; y: number}>>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [finalConsent, setFinalConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadGoogleFonts(); }, []);

  useEffect(() => {
    if (signatureMode === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
    }
  }, [signatureMode]);

  const handleScroll = useCallback(() => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50 && !sectionsViewed[currentSection]) {
      setSectionsViewed(prev => ({ ...prev, [currentSection]: true }));
    }
  }, [currentSection, sectionsViewed]);

  useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [currentSection]);

  // Lock body scroll AND stop Lenis when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      // Stop Lenis smooth scroll
      const lenis = (window as any).__lenis;
      if (lenis && typeof lenis.stop === 'function') {
        lenis.stop();
      }

      // Block ALL wheel events on body to prevent Lenis interference
      const blockWheel = (e: WheelEvent) => {
        // Only allow scroll inside the modal content area
        const target = e.target as HTMLElement;
        const isInsideModal = target.closest('[data-modal-content="true"]');
        if (!isInsideModal) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      document.addEventListener('wheel', blockWheel, { passive: false, capture: true });

      return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        document.removeEventListener('wheel', blockWheel, true);
        // Re-enable Lenis smooth scroll
        const lenisInstance = (window as any).__lenis;
        if (lenisInstance && typeof lenisInstance.start === 'function') {
          lenisInstance.start();
        }
      };
    }
  }, [isOpen]);

  const getPosition = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (signatureMode !== 'draw') return;
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPosition(e);
    setCurrentPath([pos]);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) { ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath(); ctx.moveTo(pos.x, pos.y); }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || signatureMode !== 'draw') return;
    e.preventDefault();
    const pos = getPosition(e);
    setCurrentPath(prev => [...prev, pos]);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) { ctx.lineTo(pos.x, pos.y); ctx.stroke(); }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentPath.length > 1) {
      setPaths(prev => [...prev, currentPath]);
      if (canvasRef.current) setSignatureData(canvasRef.current.toDataURL('image/png'));
    }
    setCurrentPath([]);
  };

  const clearCanvas = () => {
    setPaths([]); setCurrentPath([]); setSignatureData(null);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  };

  const generateTypedSignature = useCallback(() => {
    if (!typedName.trim()) return null;
    const canvas = document.createElement('canvas');
    canvas.width = 400; canvas.height = 100;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 400, 100);
      ctx.fillStyle = '#000000'; ctx.font = `40px ${selectedFont.family}`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(typedName, 200, 50);
      return canvas.toDataURL('image/png');
    }
    return null;
  }, [typedName, selectedFont]);

  const allSectionsViewed = sectionsViewed.terms && sectionsViewed.privacy && sectionsViewed.cookies;
  const hasSignature = signatureMode === 'draw' ? paths.length > 0 : typedName.trim().length > 0;
  const canSubmit = allSectionsViewed && hasSignature && finalConsent;

  const handleAccept = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const signaturePng = signatureMode === 'draw' ? signatureData || '' : generateTypedSignature() || '';
      const acceptanceData: AcceptanceData = {
        accepted: true, timestamp: new Date().toISOString(),
        signerName: typedName || userInfo.name, signerEmail: userInfo.email,
        signatureType: signatureMode, signaturePng,
        signatureText: signatureMode === 'type' ? typedName : undefined,
        signatureFont: signatureMode === 'type' ? selectedFont.name : undefined,
        sectionsViewed, userAgent: navigator.userAgent,
        legalVersion: LEGAL_VERSION
      };
      onAccept(acceptanceData);
    } catch (error) {
      console.error('Failed to process acceptance:', error);
    } finally { setSubmitting(false); }
  };

  if (!isOpen) return null;

  const sections = ['terms', 'privacy', 'cookies'] as const;
  const currentSectionData = LEGAL_SECTIONS[currentSection];

  return (
    <>
      {/* Overlay */}
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => {
          // Allow touch scroll only inside modal content
          const target = e.target as HTMLElement;
          if (!target.closest('[data-modal-content="true"]')) {
            e.preventDefault();
          }
        }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,2,15,0.85)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px', backdropFilter: 'blur(8px)',
          touchAction: 'none',
        }}
      >
        {/* Modal */}
        <div style={{
          background: THEME.body, borderRadius: '16px',
          boxShadow: `0 25px 60px rgba(0,0,0,0.6), 0 0 0 1px ${THEME.accentDim}`,
          width: '100%', maxWidth: '800px', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          color: THEME.text,
        }}>
          {/* Header */}
          <div style={{
            background: THEME.gradient,
            padding: '24px 28px', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#fff' }}>
                  Legal Agreement Acceptance
                </h2>
                <p style={{ margin: '6px 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                  Please review and accept all terms to continue with {planName}
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px',
                  color: '#fff', width: '36px', height: '36px', cursor: 'pointer',
                  fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Section tabs */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
              {sections.map((section) => (
                <button
                  key={section}
                  onClick={() => setCurrentSection(section)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 16px', borderRadius: '8px', border: 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                    transition: 'all 0.2s',
                    background: currentSection === section ? '#fff' : 'rgba(255,255,255,0.15)',
                    color: currentSection === section ? THEME.body : '#fff',
                  }}
                >
                  <i className={`fas ${LEGAL_SECTIONS[section].icon}`}></i>
                  <span>{LEGAL_SECTIONS[section].title}</span>
                  {sectionsViewed[section] && (
                    <i className="fas fa-check-circle" style={{ color: currentSection === section ? '#198754' : THEME.accent, fontSize: '14px' }}></i>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content area - entire middle section scrolls */}
          <div
            ref={contentRef}
            data-modal-content="true"
            onScroll={handleScroll}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            style={{
              flex: 1, overflowY: 'scroll',
              minHeight: 0,
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
            }}
          >
            {/* Legal content area */}
            <div style={{ padding: '24px 28px', background: THEME.panel }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: THEME.text, marginBottom: '16px', marginTop: 0 }}>
                <i className={`fas ${currentSectionData.icon}`} style={{ marginRight: '10px', color: THEME.accent }}></i>
                {currentSectionData.title}
              </h3>
              <div className="seal-legal-content">
                <currentSectionData.Body />
              </div>
              {!sectionsViewed[currentSection] && (
                <div style={{
                  position: 'sticky', bottom: 0, textAlign: 'center',
                  padding: '12px 0 4px',
                  background: `linear-gradient(transparent, ${THEME.panel} 40%)`,
                }}>
                  <span style={{ fontSize: '13px', color: THEME.textMuted }}>
                    <i className="fas fa-chevron-down" style={{ marginRight: '6px' }}></i>
                    Scroll to read all content
                  </span>
                </div>
              )}
            </div>

            {/* Signature Section - now inside scrollable area */}
            <div style={{
              borderTop: `1px solid ${THEME.border}`, padding: '20px 28px',
              background: THEME.body,
            }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 600, color: THEME.text }}>
                Your Signature
              </h4>

              {/* Mode selector */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  onClick={() => setSignatureMode('type')}
                  style={{
                    padding: '8px 16px', borderRadius: '8px', border: 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                    background: signatureMode === 'type' ? THEME.accent : THEME.panelAlt,
                    color: signatureMode === 'type' ? THEME.body : THEME.textMuted,
                  }}
                >
                  <i className="fas fa-keyboard" style={{ marginRight: '6px' }}></i> Type
                </button>
                <button
                  onClick={() => setSignatureMode('draw')}
                  style={{
                    padding: '8px 16px', borderRadius: '8px', border: 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                    background: signatureMode === 'draw' ? THEME.accent : THEME.panelAlt,
                    color: signatureMode === 'draw' ? THEME.body : THEME.textMuted,
                  }}
                >
                  <i className="fas fa-pen" style={{ marginRight: '6px' }}></i> Draw
                </button>
              </div>

              {signatureMode === 'type' ? (
                <div>
                  <input
                    type="text"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your full name"
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: '8px',
                      border: `1px solid ${THEME.border}`, background: THEME.panel,
                      color: THEME.text, fontSize: '14px', outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    {SIGNATURE_FONTS.map((font) => (
                      <button
                        key={font.name}
                        onClick={() => setSelectedFont(font)}
                        style={{
                          padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                          border: selectedFont.name === font.name ? `2px solid ${THEME.accent}` : `1px solid ${THEME.border}`,
                          background: selectedFont.name === font.name ? THEME.accentDim : THEME.panel,
                          color: THEME.text, fontFamily: font.family, fontSize: '16px',
                        }}
                      >
                        {typedName || 'Signature'}
                      </button>
                    ))}
                  </div>
                  {typedName && (
                    <div style={{
                      marginTop: '12px', padding: '16px', background: '#fff',
                      border: `2px dashed ${THEME.border}`, borderRadius: '8px', textAlign: 'center',
                    }}>
                      <span style={{ fontFamily: selectedFont.family, fontSize: '30px', color: '#000' }}>
                        {typedName}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{
                    position: 'relative', border: `2px dashed ${THEME.border}`,
                    borderRadius: '8px', overflow: 'hidden', background: '#fff',
                  }}>
                    <canvas
                      ref={canvasRef}
                      width={400} height={100}
                      onMouseDown={startDrawing} onMouseMove={draw}
                      onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
                      style={{ width: '100%', maxWidth: '100%', height: 'auto', cursor: 'crosshair', touchAction: 'none', display: 'block' }}
                    />
                    {paths.length === 0 && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none', color: '#9ca3af',
                      }}>
                        Draw your signature here
                      </div>
                    )}
                  </div>
                  {paths.length > 0 && (
                    <button
                      onClick={clearCanvas}
                      style={{
                        marginTop: '8px', background: 'none', border: 'none',
                        color: THEME.danger, cursor: 'pointer', fontSize: '13px',
                      }}
                    >
                      <i className="fas fa-undo" style={{ marginRight: '4px' }}></i> Clear signature
                    </button>
                  )}
                </div>
              )}

              {/* Final consent */}
              <div style={{
                marginTop: '16px', padding: '14px', borderRadius: '8px',
                background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.25)',
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={finalConsent}
                    onChange={(e) => setFinalConsent(e.target.checked)}
                    style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: THEME.accent, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '13px', color: THEME.textSecondary, lineHeight: '1.5' }}>
                    I have read and agree to the <strong>Terms and Conditions</strong>,{' '}
                    <strong>Privacy Policy</strong>, and <strong>Cookie Policy</strong>.
                    I understand that my electronic signature is legally binding and I consent
                    to receiving a copy of this agreement via email.
                  </span>
                </label>
              </div>

              {!allSectionsViewed && (
                <p style={{ marginTop: '8px', fontSize: '13px', color: THEME.warning }}>
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                  Please scroll through all sections to read the complete terms
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div style={{
            borderTop: `1px solid ${THEME.border}`, padding: '16px 28px',
            background: THEME.panelAlt, display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', gap: '12px',
          }}>
            <div style={{ fontSize: '13px', color: THEME.textMuted }}>
              Signing as: <strong style={{ color: THEME.accent }}>{userInfo.email}</strong>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '10px 24px', borderRadius: '8px', cursor: 'pointer',
                  background: 'transparent', border: `1px solid ${THEME.border}`,
                  color: THEME.textMuted, fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={!canSubmit || submitting}
                style={{
                  padding: '10px 24px', borderRadius: '8px', border: 'none',
                  fontSize: '14px', fontWeight: 600, cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: canSubmit && !submitting
                    ? THEME.gradient
                    : THEME.panelAlt,
                  color: canSubmit && !submitting ? '#fff' : '#484f58',
                  opacity: canSubmit && !submitting ? 1 : 0.7,
                }}
              >
                {submitting ? (
                  <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                ) : (
                  <><i className="fas fa-check-circle"></i> Accept & Continue</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scoped styles for legal content */}
      <style>{`
        .seal-legal-content h3 {
          font-size: 15px;
          font-weight: 600;
          margin: 20px 0 8px;
          color: ${THEME.text};
        }
        .seal-legal-content h3:first-child {
          margin-top: 0;
        }
        .seal-legal-content p {
          margin: 0 0 10px;
          line-height: 1.7;
          color: ${THEME.textMuted};
          font-size: 13px;
        }
        .seal-legal-content ul {
          margin: 0 0 10px 20px;
          padding: 0;
        }
        .seal-legal-content li {
          margin-bottom: 4px;
          line-height: 1.6;
          color: ${THEME.textMuted};
          font-size: 13px;
        }
        .seal-legal-content strong {
          color: ${THEME.textSecondary};
        }
      `}</style>
    </>
  );
};

export default TermsAcceptanceModal;
export { SIGNATURE_FONTS, loadGoogleFonts };
